// client/src/coms/settings/muteHotkeys.js
// Mute hotkeys (this browser). One source of truth for Settings and the voice session:
//   nothing stored  -> the default shortcut is active
//   'none'          -> the user cleared it (no shortcut)
//   anything else   -> that shortcut
// Previously the voice session treated "nothing stored" as "no shortcut" while Settings showed (and, when
// opened, wrote) the default, so a cleared hotkey came back the next time Settings was opened.

export const MUTE_HOTKEYS = {
  mic: { key: 'vox:muteMicShortcut', fallback: 'Ctrl+Shift+M', label: 'Mute / unmute microphone' },
  speaker: { key: 'vox:muteSpeakerShortcut', fallback: 'Ctrl+Shift+D', label: 'Deafen / undeafen' },
  both: { key: 'vox:muteBothShortcut', fallback: 'Ctrl+Shift+X', label: 'Mute and deafen together' },
};

const NONE = 'none';

export function readMuteHotkey(kind) {
  const def = MUTE_HOTKEYS[kind];
  if (!def) return '';
  try {
    const raw = window.localStorage.getItem(def.key);
    if (raw == null || !String(raw).trim()) return def.fallback;
    return String(raw).trim() === NONE ? '' : String(raw).trim();
  } catch {
    return def.fallback;
  }
}

// value '' clears the shortcut; null/undefined restores the default.
export function writeMuteHotkey(kind, value) {
  const def = MUTE_HOTKEYS[kind];
  if (!def) return;
  try {
    if (value == null) window.localStorage.removeItem(def.key);
    else window.localStorage.setItem(def.key, String(value).trim() || NONE);
  } catch {}
}

export function readAllMuteHotkeys() {
  return { mic: readMuteHotkey('mic'), speaker: readMuteHotkey('speaker'), both: readMuteHotkey('both') };
}

export function announceMuteHotkeys() {
  const all = readAllMuteHotkeys();
  try {
    window.dispatchEvent(new CustomEvent('vox-shortcut-prefs-changed', { detail: { muteMicShortcut: all.mic, muteSpeakerShortcut: all.speaker, muteBothShortcut: all.both } }));
  } catch {}
}

function keyCodeToKeyPart(code) {
  const c = String(code || '').trim();
  if (!c) return '';
  if (c.startsWith('Key') && c.length === 4) return c.slice(3).toUpperCase();
  if (c.startsWith('Digit') && c.length === 6) return c.slice(5);
  return c;
}

const MODIFIER_CODES = new Set(['ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight', 'AltLeft', 'AltRight', 'MetaLeft', 'MetaRight', 'OSLeft', 'OSRight']);

// "Ctrl+Shift+M". Returns '' while only modifier keys are held, so capture waits for the real key.
export function shortcutFromKeyEvent(e) {
  if (!e || MODIFIER_CODES.has(e.code)) return '';
  const key = keyCodeToKeyPart(e.code);
  if (!key) return '';
  const parts = [];
  if (e.ctrlKey) parts.push('Ctrl');
  if (e.altKey) parts.push('Alt');
  if (e.shiftKey) parts.push('Shift');
  if (e.metaKey) parts.push('Command');
  parts.push(key);
  return parts.join('+');
}

// Shortcuts the browser or OS reserve, which a web page cannot reliably catch.
const RESERVED = new Set(['ctrl+w', 'ctrl+t', 'ctrl+n', 'ctrl+shift+n', 'ctrl+shift+t', 'ctrl+shift+w', 'ctrl+tab', 'ctrl+shift+tab', 'alt+f4', 'command+w', 'command+q', 'command+t', 'command+n']);

export function describeShortcutProblem(shortcut, others = []) {
  const s = String(shortcut || '').toLowerCase();
  if (!s) return '';
  if (!/[+]/.test(s) && !/^f\d{1,2}$/.test(s)) return 'Add Ctrl, Alt or Shift so typing that key in a message does not trigger it.';
  if (RESERVED.has(s)) return 'Your browser keeps that shortcut for itself; choose another.';
  if (others.some((o) => String(o || '').toLowerCase() === s)) return 'Another mute hotkey already uses that shortcut.';
  return '';
}
